/**
 * useDarsEvents — DARS-domain event dispatcher on top of the shared
 * platform WebSocket (`useWebSocket` from `@adaptcx/shared`).
 *
 * dars/v0.9.42 — Phase 3 of the WebSocket consolidation. Replaces the
 * portal's DARS-specific ``useOfficerWebSocket`` (`/ws/officer`). All
 * DARS-domain events now flow over the shared platform ``/ws``:
 *
 *   - ``adaptcx:ws:hearing:{id}`` — participants, recording, hearing
 *     lifecycle, transcript segments, AI Notes progress, documents.
 *   - ``adaptcx:ws:user:{id}``    — officer-scoped notifications
 *     (assignments, reschedule, decision-returned, etc.).
 *   - ``adaptcx:ws:case:{id}``    — case lifecycle (pending decision,
 *     decided, reopened).
 *
 * The backend dual-publishes to legacy ``dars:*`` channels during
 * transition — those consumers (the old ``/ws/officer`` route) still
 * work but this portal no longer opens that connection.
 *
 * Connection lifecycle is managed by ``useWebSocketLifecycle`` in
 * ``App.vue``; this composable ONLY registers channel handlers and
 * exposes the subscribe helpers.
 */
import { computed, onBeforeUnmount, onMounted } from 'vue'
import { useWebSocket } from '@adaptcx/shared'

import { useHearingsWsStore } from '@/stores/wsHearings'
import { useParticipantsWsStore } from '@/stores/wsParticipants'
import { useRecordingWsStore } from '@/stores/wsRecording'
import { useTranscriptWsStore, type TranscriptSegment } from '@/stores/wsTranscript'
import { useWsCaseStore } from '@/stores/wsCase'
import { officerCasesService } from '@/services/officerCasesService'

// Module-level singleton — handlers register ONCE regardless of how
// many components call useDarsEvents(). Multiple components sharing
// the same handlers is intentional; the store methods they invoke
// are idempotent.
let _instance: ReturnType<typeof _create> | null = null

function _create() {
  const hearingsStore = useHearingsWsStore()
  const participantsStore = useParticipantsWsStore()
  const recordingStore = useRecordingWsStore()
  const transcriptStore = useTranscriptWsStore()
  const caseStore = useWsCaseStore()

  const ws = useWebSocket()

  // Handler dispatches on the envelope's event type. Same table shape
  // useOfficerWebSocket carried today (lifted verbatim from
  // useOfficerWebSocket.ts::handleMessage). Envelope shape is now the
  // shared WebSocketMessage: {id, type, timestamp, channel, target,
  // payload}. This handler is registered on the 'hearing', 'user',
  // and 'case' channels — a single event type may arrive multiple
  // times if the backend published to overlapping channels; store
  // methods are idempotent so double-dispatch is safe.
  function handleMessage(msg: any) {
    if (!msg || typeof msg !== 'object') return
    const type = msg.type
    const payload = msg.payload || {}
    const at = msg.timestamp

    switch (type) {
      // ── Session lifecycle ────────────────────────────────────────
      case 'connect_ack':
      case 'connected':
      case 'heartbeat':
      case 'hearing.subscribed':
      case 'hearing.unsubscribed':
      case 'error':
        return

      // ── Hearing lifecycle ────────────────────────────────────────
      case 'hearing.scheduled':
      case 'hearing.rescheduled':
      case 'hearing.approved':
        hearingsStore.applyPatch(payload.hearing_id, payload, at)
        return
      case 'hearing.dial_initiated':
        // v0.9.56 — officer clicked Start Hearing; server stamped
        // dial_initiated_at but the true 'in_progress' transition still
        // waits on the first call.answered webhook. No client-side state
        // change needed here — the button already shows "Starting…" via
        // its local busy flag. Handled explicitly so the dispatcher
        // doesn't log an unhandled-event warning.
        return
      case 'hearing.started':
      case 'hearing.actually_started':
        hearingsStore.markStatus(payload.hearing_id, 'in_progress', at)
        return
      case 'hearing.paused':
        hearingsStore.markStatus(payload.hearing_id, 'paused', at)
        return
      case 'hearing.resumed':
        hearingsStore.markStatus(payload.hearing_id, 'in_progress', at)
        return
      case 'hearing.ended':
        hearingsStore.markStatus(payload.hearing_id, 'concluded', at)
        return
      case 'hearing.cancelled':
        hearingsStore.markStatus(payload.hearing_id, 'cancelled', at)
        return
      case 'hearing.disposition_rendered':
        hearingsStore.markStatus(payload.hearing_id, 'rendered', at)
        return
      case 'hearing.decision_approved':
        hearingsStore.markStatus(payload.hearing_id, 'approved', at)
        return
      case 'hearing.decision_returned':
        // Reverts to 'concluded' — officer needs to revise.
        hearingsStore.markStatus(payload.hearing_id, 'concluded', at)
        return

      // ── Case lifecycle ──────────────────────────────────────────
      case 'case.pending_decision': {
        const caseId = payload.case_id
        if (!caseId) return
        officerCasesService.get(caseId)
          .then(c => caseStore.addPending(c))
          .catch(() => { /* case might not be visible to this officer; ignore */ })
        return
      }
      case 'case.decided':
        if (payload.case_id) {
          caseStore.setStatus(payload.case_id, 'decided')
        }
        return
      case 'case.reopened':
        if (payload.case_id) {
          caseStore.setStatus(payload.case_id, 'in_progress')
        }
        return

      // ── AI Notes progress ───────────────────────────────────────
      case 'ai_notes.started':
      case 'ai_notes.progress':
      case 'ai_notes.completed':
      case 'ai_notes.failed':
        // Consumed via a dedicated composable in RenderDecisionView.
        // Silent at the top-level dispatcher.
        return

      // ── Participants ─────────────────────────────────────────────
      case 'participant.added':
      case 'participant.updated':
        participantsStore.applyPatch({
          hearing_id: payload.hearing_id,
          participant_id: payload.participant_id,
          ...payload,
          last_event_at: at,
        })
        return
      case 'participant.removed':
        participantsStore.remove(payload.hearing_id, payload.participant_id)
        return
      case 'participant.dialing':
      case 'participant.ringing':
        participantsStore.applyPatch({
          hearing_id: payload.hearing_id,
          participant_id: payload.participant_id,
          call_state: type === 'participant.ringing' ? 'ringing' : 'dialing',
          call_id: payload.call_id ?? null,
          last_event_at: at,
        })
        return
      case 'participant.answered':
        participantsStore.applyPatch({
          hearing_id: payload.hearing_id,
          participant_id: payload.participant_id,
          call_state: 'connected',
          last_event_at: at,
        })
        return
      case 'participant.ended':
      case 'participant.no_answer':
      case 'participant.busy':
      case 'participant.failed':
      case 'participant.disconnected':
        participantsStore.applyPatch({
          hearing_id: payload.hearing_id,
          participant_id: payload.participant_id,
          call_state: 'disconnected',
          last_event_at: at,
        })
        return
      case 'participant.muted':
        participantsStore.applyPatch({
          hearing_id: payload.hearing_id,
          participant_id: payload.participant_id,
          is_muted: true,
          last_event_at: at,
        })
        return
      case 'participant.unmuted':
        participantsStore.applyPatch({
          hearing_id: payload.hearing_id,
          participant_id: payload.participant_id,
          is_muted: false,
          last_event_at: at,
        })
        return
      case 'participant.held':
        participantsStore.applyPatch({
          hearing_id: payload.hearing_id,
          participant_id: payload.participant_id,
          is_on_hold: true,
          last_event_at: at,
        })
        return
      case 'participant.resumed':
        participantsStore.applyPatch({
          hearing_id: payload.hearing_id,
          participant_id: payload.participant_id,
          is_on_hold: false,
          last_event_at: at,
        })
        return
      case 'participant.bulk_state_change':
        if (Array.isArray(payload.participants)) {
          for (const p of payload.participants) {
            participantsStore.applyPatch({
              hearing_id: payload.hearing_id,
              participant_id: p.id || p.participant_id,
              is_muted: p.is_muted,
              is_on_hold: p.is_on_hold,
              last_event_at: at,
            })
          }
        }
        return

      // ── Recording ────────────────────────────────────────────────
      case 'recording.started':
        recordingStore.setState(payload.hearing_id, 'recording')
        return
      case 'recording.stopped':
        recordingStore.setState(payload.hearing_id, 'stopped')
        return
      case 'recording.paused':
        recordingStore.setState(payload.hearing_id, 'paused')
        return
      case 'recording.resumed':
        recordingStore.setState(payload.hearing_id, 'recording')
        return
      case 'recording.error':
        recordingStore.setState(payload.hearing_id, 'error', payload.error_message)
        return

      // ── Transcript segments (now on shared /ws — no per-hearing bridge) ──
      case 'transcript.segment': {
        const seg: TranscriptSegment = {
          hearing_id: payload.hearing_id,
          timestamp: payload.timestamp || at,
          text: payload.text || '',
          participant_name: payload.participant_name ?? null,
          participant_id: payload.participant_id ?? null,
          speaker_role: payload.speaker_role ?? null,
          is_final: payload.is_final,
        }
        transcriptStore.appendSegment(seg)
        return
      }

      // ── Documents ────────────────────────────────────────────────
      case 'document.added':
      case 'document.updated':
      case 'document.removed':
        // No dedicated document store yet — Ship 3 adds one. For now,
        // callers who care can poll a REST endpoint. Silent.
        return

      default:
        // Unknown message type — log and continue.
        // eslint-disable-next-line no-console
        console.debug('[useDarsEvents] unhandled event type:', type)
    }
  }

  // Register the handler on every DARS-relevant channel. Duplicate
  // dispatch for events published to multiple channels (e.g., a case
  // event that lands on both case: and user:) is safe because the
  // store methods are idempotent.
  const unsubHearing = ws.onChannel('hearing', handleMessage)
  const unsubUser = ws.onChannel('user', handleMessage)
  const unsubCase = ws.onChannel('case', handleMessage)

  function subscribeHearing(hearingId: string) {
    if (!hearingId) return
    ws.subscribeToHearing(hearingId)
  }

  function unsubscribeHearing(hearingId: string) {
    if (!hearingId) return
    ws.unsubscribeFromHearing(hearingId)
  }

  function subscribeCase(caseId: string) {
    if (!caseId) return
    ws.subscribeToCase(caseId)
  }

  function unsubscribeCase(caseId: string) {
    if (!caseId) return
    ws.unsubscribeFromCase(caseId)
  }

  function cleanup() {
    unsubHearing()
    unsubUser()
    unsubCase()
    hearingsStore.reset()
    participantsStore.reset()
    recordingStore.reset()
    transcriptStore.reset()
  }

  return {
    isConnected: computed(() => ws.isConnected.value),
    subscribeHearing,
    unsubscribeHearing,
    subscribeCase,
    unsubscribeCase,
    cleanup,
  }
}

export function useDarsEvents() {
  if (!_instance) {
    _instance = _create()
  }
  return _instance!
}

/** Convenience — call from view onMounted / onBeforeUnmount to auto
 *  subscribe / unsubscribe for the currently-viewed hearing. Preserves
 *  the API from useOfficerWebSocket so consumers don't change. */
export function useHearingSubscription(hearingIdRef: () => string | undefined) {
  const events = useDarsEvents()
  onMounted(() => {
    const id = hearingIdRef()
    if (id) events.subscribeHearing(id)
  })
  onBeforeUnmount(() => {
    const id = hearingIdRef()
    if (id) events.unsubscribeHearing(id)
  })
}

/** Same shape for case subscription (new — case channel added in
 *  core/v2.7.64 backend + core/v2.7.66 frontend helper). */
export function useCaseSubscription(caseIdRef: () => string | undefined) {
  const events = useDarsEvents()
  onMounted(() => {
    const id = caseIdRef()
    if (id) events.subscribeCase(id)
  })
  onBeforeUnmount(() => {
    const id = caseIdRef()
    if (id) events.unsubscribeCase(id)
  })
}
